import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAudio } from '../context/AudioContext';
import { useI18n } from '../i18n';
import { useTouchLikeInteraction } from '../hooks/useTouchLikeInteraction';
import './ReadableText.css';

const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

/**
 * Wraps app-generated text with a Caja Mutante-style red reading point.
 * The point is intentionally only revealed on hover/focus so it does not
 * contaminate dense layouts, but it keeps a 44px hit target for touch users.
 */
export default function ReadableText({
  text,
  children,
  className = '',
  label,
  alwaysShowPoint = false,
}) {
  const audio = useAudio();
  const { t } = useI18n();
  const isTouchLike = useTouchLikeInteraction();
  const [armed, setArmed] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const readableText = useMemo(() => normalizeText(text ?? children), [text, children]);
  const resolvedLabel = label || t('audio.readWithAgent');

  // If audio stops speaking globally, we reset our local active state
  useEffect(() => {
    if (!audio?.isSpeaking) {
      setIsActive(false);
    }
  }, [audio?.isSpeaking]);

  if (!readableText) return children || null;

  const handleRead = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsActive(true);
    audio?.speak?.(readableText, {
      label: resolvedLabel,
      onEnd: () => setIsActive(false),
      onError: () => setIsActive(false),
    });
  };

  const showPoint = armed || alwaysShowPoint || isTouchLike;
  const isPlaying = isActive && audio?.isSpeaking;

  return (
    <span
      className={`readable-text ${alwaysShowPoint ? 'readable-text--always-point' : ''} ${isTouchLike ? 'is-touch-like' : ''} ${className}`.trim()}
      onMouseEnter={() => !isTouchLike && setArmed(true)}
      onMouseLeave={() => !isTouchLike && setArmed(false)}
      onFocus={() => setArmed(true)}
      onBlur={() => setArmed(false)}
    >
      <span className="readable-text__content">{children ?? text}</span>
      <AnimatePresence>
        {showPoint && (
          <motion.button
            type="button"
            className={`readable-text__point ${isPlaying ? 'is-playing' : ''} ${isActive ? 'is-active' : ''}`.trim()}
            aria-label={resolvedLabel}
            title={resolvedLabel}
            onClick={handleRead}
            initial={isTouchLike ? { opacity: 0.26, scale: 1 } : { opacity: 0, scale: 0.45 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={isTouchLike ? { opacity: 0.26 } : { opacity: 0, scale: 0.55 }}
            transition={{ type: 'spring', stiffness: 420, damping: 24 }}
          >
            <span className="readable-text__point-core">▶</span>
          </motion.button>
        )}
      </AnimatePresence>
    </span>
  );
}
